import React, { useState, useEffect } from "react";
import { Modal, Form, InputNumber, Input, Button, DatePicker } from "antd";
import type { Referral } from "../../../../core/data/redux/recruitment/types";

interface ApproveBonusModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (referralId: number, amount: number, notes?: string) => Promise<void>;
  referral: Referral | null;
}

export const ApproveBonusModal: React.FC<ApproveBonusModalProps> = ({
  show,
  onClose,
  onSubmit,
  referral,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && referral) {
      form.setFieldsValue({
        amount: referral.bonus_amount || null,
        notes: "",
      });
    } else {
      form.resetFields();
    }
  }, [show, referral, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onSubmit(referral!.id, values.amount, values.notes);
      onClose();
    } catch (err) { } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Approve Referral Bonus"
      open={show}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="Approve"
    >
      <div className="mb-3">
        <p className="text-muted fs-14">
          Approving bonus for <strong>{referral?.candidate_name}</strong> referred by{" "}
          <strong>{referral?.referrer_name}</strong>.
        </p>
      </div>
      <Form form={form} layout="vertical">
        <Form.Item
          name="amount"
          label="Bonus Amount (₹)"
          rules={[{ required: true, message: "Please enter the bonus amount" }]}
        >
          <InputNumber
            className="w-100"
            min={0}
            step={100}
            formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          />
        </Form.Item>
        <Form.Item name="notes" label="Approval Notes">
          <Input.TextArea rows={3} placeholder="Add any qualitative notes..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

interface ProcessPaymentModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (
    referralId: number,
    reference: string,
    date: string,
    notes?: string
  ) => Promise<void>;
  referral: Referral | null;
}

export const ProcessPaymentModal: React.FC<ProcessPaymentModalProps> = ({
  show,
  onClose,
  onSubmit,
  referral,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && referral) {
      form.setFieldsValue({
        reference: "",
        date: null,
        notes: "",
      });
    } else {
      form.resetFields();
    }
  }, [show, referral, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onSubmit(
        referral!.id,
        values.reference,
        values.date.format("YYYY-MM-DD"),
        values.notes
      );
      onClose();
    } catch (err) { } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Process Referral Payment"
      open={show}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="Mark as Paid"
    >
      <div className="mb-3">
        <p className="text-muted fs-14">
          Marking {referral?.bonus_amount ? `₹${referral.bonus_amount.toLocaleString()}` : 'bonus'} as paid for{" "}
          <strong>{referral?.candidate_name}</strong>.
        </p>
      </div>
      <Form form={form} layout="vertical">
        <Form.Item
          name="reference"
          label="Payment Reference"
          rules={[{ required: true, message: "Please provide a payment reference / transaction ID" }]}
        >
          <Input placeholder="e.g. TXN-12345XYZ" />
        </Form.Item>
        <Form.Item
          name="date"
          label="Payment Date"
          rules={[{ required: true, message: "Please select a payment date" }]}
        >
          <DatePicker className="w-100" />
        </Form.Item>
        <Form.Item name="notes" label="Payment Notes">
          <Input.TextArea rows={3} placeholder="Add any ledger or accounting notes..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};
